import httpClient from './httpClient'
import { getDateRangeInTimeZone } from '@/utils/dateTimeUtils'
import { buildCallLogsFilter, BUYER_CALL_LOGS_FILTER_FIELDS } from '@/utils/callLogsFilter'

const END_POINT = '/reporting/buyer'

// Spread into a request body: adds `filter` only when something restricts the calls
function filterBody(filter) {
  const built = buildCallLogsFilter(filter, BUYER_CALL_LOGS_FILTER_FIELDS)

  return built ? { filter: built } : {}
}

const BuyerCallLogsAPI = {
  // sortDescending: by call date; the API puts the newest first when it is left out
  async getBuyerCallLogs(accountId, { from, to, timeZone, pageSize, pageNumber, sortDescending, filter }) {
    const url = `${END_POINT}/${accountId}/calls`

    const { fromTime, toTime } = getDateRangeInTimeZone([from, to], timeZone)

    return httpClient.post(url, {
      dateFrom: fromTime,
      dateTo: toTime,
      timeZone,
      pageSize,
      pageNumber,
      sortDescending,
      ...filterBody(filter)
    })
  },

  async getBuyerTotalCalls(accountId, { from, to, timeZone, filter }) {
    const url = `${END_POINT}/${accountId}/total`

    const { fromTime, toTime } = getDateRangeInTimeZone([from, to], timeZone)

    return httpClient.post(url, {
      dateFrom: fromTime,
      dateTo: toTime,
      ...filterBody(filter)
    })
  },

  // Only the number of calls under the filter: cheap enough to ask every few seconds
  async getBuyerCallCount(accountId, { from, to, timeZone, filter }) {
    const url = `${END_POINT}/${accountId}/calls/count`

    const { fromTime, toTime } = getDateRangeInTimeZone([from, to], timeZone)

    return httpClient.post(url, {
      dateFrom: fromTime,
      dateTo: toTime,
      ...filterBody(filter)
    })
  },

  async exportBuyerCalls(accountId, { from, to, timeZone, filter }) {
    const url = `${END_POINT}/${accountId}/export`

    const { fromTime, toTime } = getDateRangeInTimeZone([from, to], timeZone)

    return httpClient.post(url, {
      dateFrom: fromTime,
      dateTo: toTime,
      timeZone,
      ...filterBody(filter)
    })
  },

  // The channels, traffic sources and campaigns of the account's calls inside the window
  async getCallFilterOptions(accountId, { from, to, timeZone }) {
    const url = `${END_POINT}/${accountId}/calls/filter-options`

    const { fromTime, toTime } = getDateRangeInTimeZone([from, to], timeZone)

    return httpClient.post(url, {
      dateFrom: fromTime,
      dateTo: toTime,
      timeZone
    })
  },

  async blockChannel(accountId, {callId, channel}) {
    const url = `${END_POINT}/${accountId}/channel/block`    

    return await httpClient.post(url, {
      callId: callId,
      channel: channel
    })
  },

  async unblockChannel(accountId, { channelName }) {
    const url = `${END_POINT}/${accountId}/channel/unblock`    

    return await httpClient.post(url, {      
      encodedName: channelName
    })
  },

  async getBlockedChannels({accountId}) {
    const url = `${END_POINT}/${accountId}/channel`
    return await httpClient.get(url)
  },

  async getBuyerDisputedCalls(accountId, { from, to, timeZone, pageSize, pageNumber }) {
    const url = `${END_POINT}/${accountId}/disputed-calls`

    const { fromTime, toTime } = getDateRangeInTimeZone([from, to], timeZone)

    return httpClient.post(url, {
      dateFrom: fromTime,
      dateTo: toTime,
      timeZone,
      pageSize,
      pageNumber
    })
  },
}

export default BuyerCallLogsAPI